import React from 'react';
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import DashboardTasksSummaryCell
    from "v4/pages/front/Dashboard/components/DashboardTasksSummary/components/DashboardTasksSummaryCell/DashboardTasksSummaryCell";
import useDashboardTasksSummary
    from "v4/pages/front/Dashboard/components/DashboardTasksSummary/hooks/useDashboardTasksSummary";
import ErrorBoundary from "components/Error/errorBoundary";
import useIsInView from "v4/hooks/useIsInView";
import {PolIcon} from "components/PolIcon/policon";
import ReactTooltip from "react-tooltip";
import {useFrontContext} from "v4/contexts/FrontContext";

export default function DashboardTasksSummary() {
    const {t} = usePolTranslation();
    const {a11yValues} = useFrontContext();
    const [ref, isInView] = useIsInView();
    const {
        data,
        state,
        tableInfos,
        getPreSearch,
        toggleRefresh,
    } = useDashboardTasksSummary(isInView);

    return (
        <ErrorBoundary canRetry>
            <div className="dashboard-tasks-summary" ref={ref}>
                <p>
                    {t('tasks_results')}
                    {state.isError && (
                        <>
                            <PolIcon icon="exclamation-circle"
                                     className="ml-2 icon-error"
                                     data-for="summary-quotes-error-tooltip"
                                     data-tip={t('server_error')}
                            />
                            <ReactTooltip id="summary-quotes-error-tooltip" effect="solid" wrapper="span"/>
                            <PolIcon icon="refresh"
                                     className="ml-2"
                                     style={{cursor: 'pointer'}}
                                     data-for="summary-quotes-refresh-tooltip"
                                     data-tip={t('retry')}
                                     onClick={toggleRefresh}
                            />
                            <ReactTooltip id="summary-quotes-refresh-tooltip" effect="solid" wrapper="span"/>
                        </>
                    )}
                </p>
                <table className={`${a11yValues?.contrasted_table ? 'pol-table--contrasted' : ''}`}>
                    <thead>
                    <tr>
                        <th/>
                        <th>{t('completed_tasks')}</th>
                        <th>{t('tasks_late')}</th>
                        <th colSpan={3}>{t('tasks_to_do')}</th>
                    </tr>
                    <tr>
                        <th/>
                        <th>{t('since_january')}</th>
                        <th/>
                        {tableInfos?.remaining_months && (
                            tableInfos.remaining_months.map(month => (
                                <th key={month}>{month}</th>
                            ))
                        )}
                    </tr>
                    </thead>
                    <tbody>
                    {state.isLoading && !state.isError && (
                        <tr>
                            {Array.from({length: 3 + (tableInfos?.remaining_months?.length ?? 0)}).map((_, index) => (
                                <DashboardTasksSummaryCell key={index} value={<PolIcon icon="circle-notch" className="spinner"/>}/>
                            ))}
                        </tr>
                    )}
                    {data && data.map((row, dataIndex) => (
                        <tr key={dataIndex}>
                            <DashboardTasksSummaryCell value={row.fullname}/>
                            <DashboardTasksSummaryCell value={row.closed}
                                                       preSearch={getPreSearch('since_january', dataIndex)}/>
                            <DashboardTasksSummaryCell value={row.late}
                                                       preSearch={getPreSearch('late', dataIndex)}/>
                            {tableInfos?.remaining_months && (
                                tableInfos.remaining_months.map((month, index) => (
                                    <DashboardTasksSummaryCell key={month}
                                                               value={row[`m_plus_${index}`]}
                                                               preSearch={getPreSearch(`m_plus_${index}`, dataIndex)}/>
                                ))
                            )}
                        </tr>
                    ))}
                    </tbody>
                    <tfoot>
                    <tr className="table-stat__body__total">
                        <td>{t('total_euro')}</td>
                        {
                            (state.isLoading && !state.isError)
                                ? (
                                    Array.from({length: 2 + (tableInfos?.remaining_months?.length ?? 0)}).map((_, index) => (
                                        <DashboardTasksSummaryCell
                                            key={index}
                                            value={<PolIcon icon="circle-notch" className="spinner"/>}/>
                                    ))
                                ) : (
                                    <>
                                        <DashboardTasksSummaryCell value={tableInfos.totalByColumn.total_closed}
                                                                   preSearch={getPreSearch('total_since_january')}/>
                                        <DashboardTasksSummaryCell value={tableInfos.totalByColumn.total_late}
                                                                   preSearch={getPreSearch('total_late')}/>
                                        {tableInfos?.remaining_months && (
                                            tableInfos.remaining_months.map((month, index) => (
                                                <DashboardTasksSummaryCell key={month}
                                                                           value={tableInfos.totalByColumn[`total_m_plus_${index}`]}
                                                                           preSearch={getPreSearch(`total_m_plus_${index}`)}/>
                                            ))
                                        )}
                                    </>
                                )
                        }
                    </tr>
                    </tfoot>
                </table>
            </div>
        </ErrorBoundary>
    );
}
